import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ApiError, deleteSsoConfig, getSsoConfig, putSsoConfig } from './api/client';
import type { IdpProtocol, SsoConfigResponse } from './api/types';

// The protocols the backend accepts (IdpProtocol).
const PROTOCOLS: readonly IdpProtocol[] = ['Oidc', 'Saml', 'Oauth2'];

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

/** GET returns this code (404) when the tenant simply has no config yet — a normal empty state. */
function isNotConfigured(e: unknown): boolean {
  return e instanceof ApiError && e.body.code === 'TenantSsoConfig.NotConfigured';
}

const row = { display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.35rem 0' } as const;

/**
 * Manage one tenant's single sign-on configuration (view / create-or-replace / remove). There is at
 * most one config per tenant, so this is a single form rather than a list. Auth is deferred (ADR-011):
 * this is configuration the later auth epic consumes; the client secret is a Key Vault reference, never
 * the raw secret.
 */
export function SsoConfigPanel({ tenant }: { tenant: string }) {
  const [config, setConfig] = useState<SsoConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // form fields
  const [protocol, setProtocol] = useState<IdpProtocol>('Oidc');
  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecretRef, setClientSecretRef] = useState('');
  const [scopes, setScopes] = useState('');
  const [metadataUrl, setMetadataUrl] = useState('');
  const [enabled, setEnabled] = useState(true);

  // Mirror a loaded config (or a cleared "not configured" state) into the form fields.
  function applyConfig(c: SsoConfigResponse | null): void {
    setConfig(c);
    setProtocol(c?.protocol ?? 'Oidc');
    setIssuer(c?.issuer ?? '');
    setClientId(c?.clientId ?? '');
    setClientSecretRef(c?.clientSecretRef ?? '');
    setScopes(c ? c.scopes.join(' ') : '');
    setMetadataUrl(c?.metadataUrl ?? '');
    setEnabled(c ? c.enabled : true);
  }

  async function load(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      applyConfig(await getSsoConfig(tenant));
    } catch (e: unknown) {
      if (isNotConfigured(e)) {
        applyConfig(null);
      } else {
        setError(describeError(e));
      }
    } finally {
      setBusy(false);
    }
  }

  // Reload whenever the acting-as tenant changes.
  useEffect(() => {
    void load();
  }, [tenant]);

  async function onSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const trimmedMeta = metadataUrl.trim();
      await putSsoConfig(tenant, {
        protocol,
        issuer,
        clientId,
        clientSecretRef,
        scopes: scopes.split(/\s+/).filter((s) => s.length > 0),
        metadataUrl: trimmedMeta === '' ? null : trimmedMeta,
        enabled,
      });
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function onRemove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteSsoConfig(tenant);
      await load();
    } catch (err: unknown) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>
        SSO for &ldquo;{tenant}&rdquo;{' '}
        <button onClick={() => void load()} disabled={busy}>
          Reload
        </button>
      </h2>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      {config === null ? (
        <p>
          <em>No SSO configured for &ldquo;{tenant}&rdquo;.</em>
        </p>
      ) : (
        <p>
          Configured — protocol <strong>{config.protocol}</strong>, {config.enabled ? 'enabled' : 'disabled'}.
        </p>
      )}

      <form onSubmit={(e) => void onSave(e)}>
        <div style={row}>
          <label>
            Protocol{' '}
            <select value={protocol} onChange={(e) => setProtocol(e.currentTarget.value as IdpProtocol)}>
              {PROTOCOLS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={row}>
          <input
            required
            placeholder="issuer"
            value={issuer}
            onChange={(e) => setIssuer(e.currentTarget.value)}
          />
        </div>
        <div style={row}>
          <input
            required
            placeholder="client id"
            value={clientId}
            onChange={(e) => setClientId(e.currentTarget.value)}
          />
        </div>
        <div style={row}>
          <input
            required
            placeholder="client secret reference (Key Vault)"
            value={clientSecretRef}
            onChange={(e) => setClientSecretRef(e.currentTarget.value)}
          />
        </div>
        <div style={row}>
          <input
            placeholder="scopes (space-separated)"
            value={scopes}
            onChange={(e) => setScopes(e.currentTarget.value)}
          />
        </div>
        <div style={row}>
          <input
            placeholder="metadata URL (optional)"
            value={metadataUrl}
            onChange={(e) => setMetadataUrl(e.currentTarget.value)}
          />
        </div>
        <div style={row}>
          <label>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.currentTarget.checked)}
            />{' '}
            Enabled
          </label>
        </div>
        <div style={row}>
          <button type="submit" disabled={busy}>
            Save
          </button>
          {config !== null && (
            <button type="button" onClick={() => void onRemove()} disabled={busy}>
              Remove
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
